import { useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconRefresh, IconThumbDown, IconThumbUp, IconX } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ResponseFeedback } from '~api';
import { api } from '~portal/api';
import { AnchorLink, ButtonLink } from '~portal/components/common';
import { preWrap } from '~portal/components/common/common.css';
import { CopyIdButton } from '~portal/components/inspector/CopyIdButton';
import { UserFeedbackList } from '~portal/components/inspector/UserFeedbackList';
import { logger } from '~portal/lib';
import { type FeedbackFilter, FeedbackFilters, parseFeedbackFilter } from '~portal/lib/inspector';

/**
 * @desc `?userId=` narrows the page to one user's ratings (every verdict, commented or not — the
 * Inspector's feedback tile links here); `?filter=` is that list's verdict/comment filter. Without
 * a user the page is the global review surface: window totals and the newest comments.
 */
type FeedbackSearch = { userId?: string; filter?: FeedbackFilter };

export const Route = createFileRoute('/_protected/admin/feedback')({
  validateSearch: (search: Record<string, unknown>): FeedbackSearch => {
    if (typeof search.userId !== 'string' || !search.userId) return {};
    const filter = parseFeedbackFilter(search.filter);
    return filter === FeedbackFilters.All
      ? { userId: search.userId }
      : { userId: search.userId, filter };
  },
  component: AdminFeedbackPage,
});

const COMMENT_FEED_LIMIT = 100;

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

/** @desc Positive and negative totals for one trailing window, with the share of positives. */
function StatsTile({ label, counts }: { label: string; counts: ResponseFeedback.Counts }) {
  const total = counts.up + counts.down;
  const positive = total > 0 ? Math.round((counts.up / total) * 100) : null;

  return (
    <Paper withBorder p="md">
      <Stack gap="xs">
        <Text size="sm" c="dimmed" fw={500}>
          {label}
        </Text>
        <Group gap="lg" align="baseline">
          <Group gap={6} align="baseline">
            <IconThumbUp size={16} aria-hidden />
            <Text size="xl" fw={700} aria-label={`${label} positive`}>
              {counts.up}
            </Text>
          </Group>
          <Group gap={6} align="baseline">
            <IconThumbDown size={16} aria-hidden />
            <Text size="xl" fw={700} aria-label={`${label} negative`}>
              {counts.down}
            </Text>
          </Group>
        </Group>
        <Text size="xs" c="dimmed">
          {positive === null ? 'No feedback yet' : `${positive}% positive of ${total}`}
        </Text>
      </Stack>
    </Paper>
  );
}

function AdminFeedbackPage() {
  const { userId } = Route.useSearch();
  return userId ? <UserFeedbackPage userId={userId} /> : <AllFeedbackPage />;
}

/** @desc One user's ratings, reached from the Inspector's feedback tile. */
function UserFeedbackPage({ userId }: { userId: string }) {
  const { filter = FeedbackFilters.All } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const emailsQuery = api.admin.getUserEmails.useQuery({ userIds: [userId] });
  const utils = api.useUtils();
  const owner = emailsQuery.data?.[userId] ?? userId;

  const setFilter = (next: FeedbackFilter) => {
    navigate({
      search: (prev) => ({ ...prev, filter: next === FeedbackFilters.All ? undefined : next }),
      replace: true,
      resetScroll: false,
    }).catch((err) => {
      logger.error('[AdminFeedback] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return (
    <Stack gap="lg" p="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="sm" wrap="wrap">
          <ButtonLink
            to="/admin/inspector"
            search={{ userId }}
            variant="subtle"
            size="compact-sm"
            leftSection={<IconArrowLeft size={14} />}
          >
            Inspector
          </ButtonLink>
          <Title order={1}>Feedback</Title>
        </Group>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconRefresh size={16} />}
          onClick={() => void utils.responseFeedback.forUser.invalidate({ userId })}
        >
          Refresh
        </Button>
      </Group>

      <Paper withBorder p="md">
        <Stack gap="md">
          <Group gap="xs" justify="space-between" wrap="wrap">
            <Group gap={4} wrap="nowrap" miw={0}>
              <Text fw={600} truncate>
                {owner}
              </Text>
              <CopyIdButton value={userId} label="user id" />
            </Group>
            <ButtonLink
              to="/admin/feedback"
              search={{}}
              variant="subtle"
              size="compact-xs"
              color="gray"
              leftSection={<IconX size={12} />}
            >
              All users
            </ButtonLink>
          </Group>
          <UserFeedbackList userId={userId} filter={filter} onFilterChange={setFilter} />
        </Stack>
      </Paper>
    </Stack>
  );
}

/** @desc The global review surface: trailing-window totals and the newest comments. */
function AllFeedbackPage() {
  const statsQuery = api.responseFeedback.stats.useQuery();
  const commentsQuery = api.responseFeedback.comments.useQuery({ limit: COMMENT_FEED_LIMIT });

  const comments = commentsQuery.data ?? [];
  const userIds = useMemo(
    () => Array.from(new Set(comments.map((c) => c.refs.userId))),
    [comments],
  );
  const emailsQuery = api.admin.getUserEmails.useQuery(
    { userIds },
    { enabled: userIds.length > 0 },
  );
  const emails = emailsQuery.data ?? {};

  const refresh = () => {
    statsQuery.refetch();
    commentsQuery.refetch();
  };

  const error = statsQuery.error ?? commentsQuery.error;

  return (
    <Stack gap="lg" p="lg">
      <Group justify="space-between" align="center">
        <Title order={1}>Feedback</Title>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconRefresh size={16} />}
          loading={statsQuery.isFetching || commentsQuery.isFetching}
          onClick={refresh}
        >
          Refresh
        </Button>
      </Group>

      {error && (
        <Alert color="red" title="Could not load feedback">
          {error.message}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {statsQuery.data ? (
          <>
            <StatsTile label="Last 7 days" counts={statsQuery.data.week} />
            <StatsTile label="Last 30 days" counts={statsQuery.data.month} />
          </>
        ) : (
          <>
            <Skeleton height={110} />
            <Skeleton height={110} />
          </>
        )}
      </SimpleGrid>

      <Paper withBorder p="md">
        <Stack gap="md">
          <Title order={3}>Comments</Title>
          {commentsQuery.isLoading ? (
            <Skeleton height={80} />
          ) : comments.length === 0 ? (
            <Text c="dimmed" size="sm">
              No comments yet.
            </Text>
          ) : (
            <Stack gap="sm">
              {comments.map((entry) => (
                <Paper key={entry._id} withBorder p="sm">
                  <Stack gap={6}>
                    <Group gap="xs" justify="space-between" wrap="wrap">
                      <Group gap="xs">
                        <Badge
                          size="sm"
                          variant="light"
                          color={entry.verdict === ResponseFeedback.verdicts.Up ? 'teal' : 'red'}
                          leftSection={
                            entry.verdict === ResponseFeedback.verdicts.Up ? (
                              <IconThumbUp size={12} />
                            ) : (
                              <IconThumbDown size={12} />
                            )
                          }
                        >
                          {entry.verdict === ResponseFeedback.verdicts.Up ? 'Positive' : 'Negative'}
                        </Badge>
                        <AnchorLink
                          to="/admin/feedback"
                          search={{ userId: entry.refs.userId }}
                          size="sm"
                          fw={500}
                        >
                          {emails[entry.refs.userId] ?? entry.refs.userId}
                        </AnchorLink>
                      </Group>
                      <Group gap="xs">
                        <Text size="xs" c="dimmed">
                          {formatDateTime(entry.updatedAt)}
                        </Text>
                        <AnchorLink
                          to="/chat/$conversationId"
                          params={{ conversationId: entry.refs.conversationId }}
                          size="xs"
                        >
                          Open conversation
                        </AnchorLink>
                        <AnchorLink
                          to="/admin/inspector/$conversationId"
                          params={{ conversationId: entry.refs.conversationId }}
                          search={entry.turnId ? { turnId: entry.turnId } : {}}
                          size="xs"
                        >
                          Inspect
                        </AnchorLink>
                      </Group>
                    </Group>
                    <Text size="sm" className={preWrap}>
                      {entry.comment}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
